import { ClockIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  forwardRef,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useRef,
} from 'react';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button } from '@/components/ui/daisy/button';
import { Dropdown } from '@/components/ui/daisy/dropdown';
import { useDropdownAutoAlign } from '@/components/ui/daisy/dropdown/useDropdownAutoAlign';
import { useDropdownClickOutside } from '@/components/ui/daisy/dropdown/useDropdownClickOutside';
import Fieldset from '@/components/ui/daisy/fieldset';
import { Colors, Sizes } from '@/components/ui/types';
import useToggle from '@/data/hook/useToggle';

import { TimePicker } from './TimePicker';
import { TimeInputProps } from './types';
import { useTimeInputDraft } from './useTimeInputDraft';
import { useTimeInputLabels } from './useTimeInputLabels';

const sizeClasses: Record<Sizes, string> = {
  xs: 'input-xs',
  sm: 'input-sm',
  md: 'input-md',
  lg: 'input-lg',
  xl: 'input-xl',
};

const colorClasses: Record<Colors, string> = {
  default: '',
  neutral: 'input-neutral',
  primary: 'input-primary',
  secondary: 'input-secondary',
  accent: 'input-accent',
  info: 'input-info',
  success: 'input-success',
  warning: 'input-warning',
  error: 'input-error',
};

const blurActiveElement = () => {
  const htmlActiveElement = document.activeElement as HTMLElement | null;
  htmlActiveElement?.blur();
};

const TimeInputBase = forwardRef<HTMLDivElement, TimeInputProps>(
  (props, ref) => {
    const labels = useTimeInputLabels();

    const {
      value,
      onChange,
      blockedTimes,
      placeholder = labels.placeholder,
      disabled = false,
      clearable = true,
      error = false,
      size = 'md',
      color = 'default',
      forceOpen = false,
      clearAriaLabel = labels.clear,
      hoursAriaLabel = labels.hours,
      minutesAriaLabel = labels.minutes,
      classNames,
    } = props;

    const panelRef = useRef<HTMLDivElement>(null);
    const { align, handleFocus } = useDropdownAutoAlign(panelRef);
    const { isOpen, open, close } = useToggle(forceOpen);

    useEffect(() => {
      if (forceOpen) open();
      else close();
    }, [forceOpen, open, close]);

    useDropdownClickOutside({
      panelRef,
      active: isOpen && !forceOpen,
      onClickOutside: close,
    });

    const { draft, handleInputChange, handleCommit, commitValue } =
      useTimeInputDraft({ value, onChange, blockedTimes });

    const openDropdown = () => {
      if (disabled) return;
      open();
      handleFocus();
    };

    const closeDropdown = () => {
      close();
      blurActiveElement();
    };

    const toggleDropdown = (event: MouseEvent<SVGSVGElement>) => {
      event.preventDefault();
      if (disabled) return;
      if (isOpen) {
        closeDropdown();
        return;
      }
      openDropdown();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') closeDropdown();
      if (event.key !== 'Enter') return;

      event.preventDefault();
      handleCommit();
      closeDropdown();
    };

    const handlePickerChange = (time: string) => {
      commitValue(time);
      closeDropdown();
    };

    return (
      <Dropdown
        ref={ref}
        open={isOpen}
        align={align}
        onKeyDown={handleKeyDown}
        className={twMerge('w-full min-w-0', classNames?.base)}
      >
        <div
          className={twMerge(
            'input w-full flex items-center gap-2',
            sizeClasses[size],
            colorClasses[color],
            error && 'input-error',
            disabled && 'input-disabled',
            classNames?.input,
          )}
        >
          <ClockIcon
            className="size-4 shrink-0 cursor-pointer opacity-50"
            onMouseDown={toggleDropdown}
          />
          <input
            type="text"
            inputMode="numeric"
            className="min-w-0 flex-1 grow bg-transparent"
            value={draft}
            placeholder={placeholder}
            disabled={disabled}
            onChange={event => handleInputChange(event.target.value)}
            onFocus={openDropdown}
            onClick={openDropdown}
            onBlur={handleCommit}
          />
          <ConditionalRenderer condition={clearable && !!value && !disabled}>
            <Button
              aria-label={clearAriaLabel}
              className="shrink-0"
              variant="ghost"
              size="xs"
              shape="circle"
              type="button"
              tabIndex={-1}
              onMouseDown={event => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={() => commitValue('')}
            >
              <XMarkIcon className="size-4 text-error" />
            </Button>
          </ConditionalRenderer>
        </div>

        <Dropdown.Content
          ref={panelRef}
          className={twMerge(
            'z-10 w-max rounded-box bg-base-100 p-2 shadow-sm mt-2 border border-base-300',
            classNames?.panel,
          )}
        >
          <TimePicker
            value={value}
            onChange={handlePickerChange}
            blockedTimes={blockedTimes}
            disabled={disabled}
            hoursAriaLabel={hoursAriaLabel}
            minutesAriaLabel={minutesAriaLabel}
          />
        </Dropdown.Content>
      </Dropdown>
    );
  },
);

TimeInputBase.displayName = 'TimeInput';

export const TimeInput = Object.assign(TimeInputBase, {
  Picker: TimePicker,
  Fieldset,
});
